import { renderWithLoader, useForm } from '@metorial/data-hooks';
import { PageHeader } from '@metorial/layout';
import { useUser } from '@metorial/state';
import { Avatar, Input, Text, toast } from '@metorial/ui';
import { Box } from '@metorial/ui-product';
import {
  Fields,
  ImageUploadButton,
  Row,
  SaveButton,
  Stack,
  useSettingsScope
} from '../../organization/pages/_kit';

// Account › Profile (cloud-spec §8). Sign-in is Google Workspace only, so the
// email can't be changed here and there are no password or SSO settings.
export let AccountPage = () => {
  let scope = useSettingsScope();
  let user = useUser();
  let updateUser = user.updateMutator();

  let form = useForm({
    initialValues: {
      firstName: (user.data as any)?.firstName ?? '',
      lastName: (user.data as any)?.lastName ?? '',
      name: user.data?.name ?? ''
    },
    enableReinitialize: true,
    onSubmit: async values => {
      let [res] = await updateUser.mutate({
        firstName: values.firstName.trim() || undefined,
        lastName: values.lastName.trim() || undefined,
        name: values.name.trim()
      });
      if (res) toast.success('Profile updated.');
    },
    schema: yup =>
      yup.object({
        name: yup.string().trim().required('Username is required')
      })
  });

  return (
    <>
      <PageHeader title="Account Settings" description="Manage your account." />

      {renderWithLoader({ user })(({ user }) => (
        <Stack>
          <Box title="Profile" description="How you appear to everyone in your organizations.">
            <Stack $gap={16}>
              <Row $gap={16}>
                <Avatar entity={user.data} size={72} />
                <ImageUploadButton
                  instanceId={scope.instanceId}
                  purpose="user_image"
                  label="Upload Profile Picture"
                  onUploaded={async imageFileId => {
                    let [res] = await updateUser.mutate({ imageFileId });
                    if (res) toast.success('Profile picture updated.');
                  }}
                />
              </Row>

              <form onSubmit={form.handleSubmit}>
                <Fields>
                  <Row $gap={14} style={{ alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <Input label="First Name" {...form.getFieldProps('firstName')} />
                    </div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <Input label="Last Name" {...form.getFieldProps('lastName')} />
                    </div>
                  </Row>
                  <div>
                    <Input label="Username" {...form.getFieldProps('name')} />
                    <form.RenderError field="name" />
                  </div>
                  <Row>
                    <SaveButton loading={updateUser.isLoading} success={updateUser.isSuccess}>
                      Update
                    </SaveButton>
                  </Row>
                </Fields>
              </form>
            </Stack>
          </Box>

          <Box title="Sign-in" description="Your account is managed by your Google Workspace.">
            <Stack $gap={4}>
              <Text size="2" weight="strong">
                {user.data.email}
              </Text>
              <Text size="2" color="gray600">
                You sign in with Google. Change your email or password in your Google Workspace
                account.
              </Text>
            </Stack>
          </Box>
        </Stack>
      ))}
    </>
  );
};
